// Libraries
import React, { useEffect, useState } from "react";
import { useObserver } from "mobx-react-lite";
import { Loader, styled } from "@shoothill/core";

// Custom
import { DetailsPage } from "Globals/Views/DetailsPage/DetailsPage.styles";
import { ControlLabel } from "Components/Form/ControlLabel";
import { Box, Typography } from "@material-ui/core";
import moment from "moment";
import { DarwinDateSelect } from "Components/DateSelect/DarwinDateSelect";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { AllIEReportViewModel } from "./AllIEReportViewModel";
import { IEItemsWrapper } from "Views/Project/Commercial/IETables/IEItemsWrapper";
import { GridHeader, SummaryGridView } from "Views/Project/Commercial/IETables/IEGrid.Styles";
import { IEGridViewCatSubDesc } from "Views/Project/Commercial/IETables/IEGridView.CatSubDesc";
import { ReportDatePickerContainer, RunReportButtonContainer } from "Views/Report/Report.styles";

// Styling & Images

moment.locale("en-GB", {
    week: {
        dow: 1,
    },
});

interface Props {
    ieId: string;
}

export const AllIEReportView: React.FunctionComponent<Props> = (props: Props) => {
    const [viewModel] = useState(() => new AllIEReportViewModel());

    //let { ieid } = router.match.params as any;

    useEffect(() => {
        return () => {
            viewModel.reset();
        };
    }, []);

    const onReportEndDateTopChanged = (date: string | null) => {
        viewModel.setValue("reportEndDateTop", date === null ? date : moment(date).startOf("day").toISOString());
        viewModel.setCanExportCSV(false);
    };

    const onReportEndDateBottomChanged = (date: string | null) => {
        viewModel.setValue("reportEndDateBottom", date === null ? date : moment(date).endOf("day").toISOString());
        viewModel.setCanExportCSV(false);
    };

    const runReport = () => {
        viewModel.runReport(props.ieId);
        viewModel.setCanExportCSV(true);
    };

    const exportCSV = () => {
        viewModel.generateIEAllReportCSV(props.ieId);
    };

    return useObserver(() => (
        <DetailsPage>
            <ReportDatePickerContainer>
                <ControlLabel label="Date comparison:" htmlFor="start-date" />
                <Box>
                    <Box maxWidth="200px">
                        <DarwinDateSelect
                            execute={(value: string | null) => onReportEndDateTopChanged(value)}
                            placeholder="Please select"
                            value={viewModel.model.reportEndDateTop}
                            maxDate={moment.utc().toDate()}
                            clearable
                            format="DD/MM/YYYY"
                            canExecute={!viewModel.IsLoading}
                            defaultToStartOfDay={true}
                        />
                    </Box>
                    <Box maxWidth="200px">
                        <DarwinDateSelect
                            execute={(value: string | null) => onReportEndDateBottomChanged(value)}
                            placeholder="Please select"
                            value={viewModel.model.reportEndDateBottom}
                            maxDate={moment.utc().toDate()}
                            clearable
                            format="DD/MM/YYYY"
                            canExecute={!viewModel.IsLoading}
                            defaultToStartOfDay={false}
                        />
                    </Box>
                </Box>
            </ReportDatePickerContainer>

            <RunReportButtonContainer>
                <PrimaryButton displayName={"Run report"} execute={runReport} fullWidth={false} canExecute={!viewModel.IsLoading} />
                <PrimaryButton displayName={"Export CSV"} execute={exportCSV} fullWidth={false} canExecute={!viewModel.IsLoading && viewModel.canExportCSV} />
            </RunReportButtonContainer>

            <IEItemsWrapper>
                <div className="content" style={{ paddingTop: "30px", position: "relative" }}>
                    <Typography variant="h2" style={{ marginLeft: "10px" }}>
                        {viewModel.getTopDateFormatted}
                    </Typography>
                    <GridHeader>
                        <SummaryGridView>
                            <li className="heading">
                                <div>Category / Sub-category / Line Description</div>
                                <div>Income</div>
                                <div>Target cost</div>
                                <div>Margin %</div>
                                <div>Committed cost</div>
                                <div>Future spend</div>
                                <div>Total expected spend</div>
                                <div>Variance</div>
                                <div>Risk/Opportunity</div>
                                <div></div>
                            </li>
                        </SummaryGridView>
                    </GridHeader>

                    {!viewModel.IsLoading && <IEGridViewCatSubDesc IEid={props.ieId} viewModel={viewModel.topGridViewModel} IEType={""} isReport={true} />}
                    {viewModel.IsLoading && <Loader />}

                    <Typography variant="h2" style={{ marginLeft: "10px" }}>
                        {viewModel.getBottomDateFormatted}
                    </Typography>
                    <GridHeader>
                        <SummaryGridView>
                            <li className="heading">
                                <div>Category / Sub-category / Line Description</div>
                                <div>Income</div>
                                <div>Target cost</div>
                                <div>Margin %</div>
                                <div>Committed cost</div>
                                <div>Future spend</div>
                                <div>Total expected spend</div>
                                <div>Variance</div>
                                <div>Risk/Opportunity</div>
                                <div></div>
                            </li>
                        </SummaryGridView>
                    </GridHeader>

                    {!viewModel.IsLoading && <IEGridViewCatSubDesc IEid={props.ieId} viewModel={viewModel.bottomGridViewModel} IEType={""} isReport={true} />}
                    {viewModel.IsLoading && <Loader />}
                </div>
            </IEItemsWrapper>
        </DetailsPage>
    ));
};
